import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { BookOpen, Footprints, Compass, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker } from "@/components/Reveal";
import { Practitioners } from "@/components/Practitioners";
import { Chip, StepProgress, StepNav } from "@/components/StepBits";
import { Field, TextArea } from "@/components/FormBits";
import { ClosingMessage, PolicyLinks, CrisisNote } from "@/components/SharedSections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORT_TYPES = ["Narrative Therapy", "Mentoring", "Fitness & Movement", "Combination", "I'm not sure"];
const SESSION_TYPES = ["Individual — Adult", "Individual — Child/Young Person", "Couple", "Family", "Parent/Carer & Child", "I'm not sure"];
const CONNECT = ["In person", "Telehealth", "Community-based", "Either / flexible"];
const AREAS = ["Relationships", "Family", "Parenting/caring", "Identity", "Culture and connection", "Grief/loss", "Stress/overwhelm", "Confidence", "Children's wellbeing", "Behaviour", "Education/school", "Communication", "Trauma/difficult experiences", "Life transitions", "Social connection", "Building routines", "Other", "I'm not sure yet"];
const PRAC_CHOICE = ["Match me with someone", "I'd like to choose a practitioner", "No preference"];
const PRAC_PREFS = ["Aboriginal practitioner", "Female practitioner", "Male practitioner", "Experience with children/young people", "Couples/family experience", "Narrative Therapy", "Cultural identity and belonging", "Trauma-informed practice", "Mentoring", "LGBTQIA+ affirming", "Fitness/movement", "Other"];

const EMPTY = {
  support_types: [], session_type: "", connect: "", preferred_days: "", preferred_times: "",
  support_areas: [], anything: "", practitioner_choice: "", practitioner_preferences: [],
  name: "", preferred_name: "", pronouns: "", email: "", phone: "", preferred_contact: "",
  age_range: "", involves_child: false, child_name: "", child_age: "", parent_carer_name: "",
  relationship_to_child: "", confirm: false,
};

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const PrivateRequestForm = ({ preset }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  useEffect(() => {
    if (preset && preset.length) setForm((f) => ({ ...f, support_types: preset }));
  }, [preset]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEv = (k) => (e) => set(k, e.target.type === "checkbox" ? e.target.checked : e.target.value);

  const next = async () => {
    if (step === 1 && form.support_types.length === 0) return toast.error("Choose at least one type of support — 'I'm not sure' is fine.");
    if (step === 2 && !form.connect) return toast.error("Let us know how you'd like to connect.");
    if (step === 5 && (!form.name.trim() || !form.email.trim())) return toast.error("Please add your name and email so we can reach you.");
    if (step < 6) return setStep(step + 1);
    if (!form.confirm) return toast.error("Please tick the confirmation box first.");
    setBusy(true);
    try {
      await axios.post(`${API}/private-requests`, form);
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Something went wrong sending that. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="private-success" className="rounded-[48px] bg-creamdark border border-ochre/25 p-12 sm:p-16 text-center">
        <CheckCircle2 className="h-12 w-12 text-terracotta mx-auto mb-6" />
        <h3 className="font-display font-light text-4xl text-charcoal">Thanks for reaching out.</h3>
        <p className="mt-5 text-earth/85 leading-relaxed max-w-xl mx-auto">
          We've received your request. One of our Aunties will review what you're looking for and contact you
          about practitioner matching, availability, fees and next steps.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="private-request-form" className="rounded-[48px] bg-creamdark border border-ochre/25 p-8 sm:p-14">
      <StepProgress step={step} total={6} />

      {step === 1 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-2">What kind of support are you looking for?</h3>
          <p className="text-sm text-earth/70 mb-6">Choose as many as fit.</p>
          <div className="flex flex-wrap gap-3">
            {SUPPORT_TYPES.map((t) => (
              <Chip key={t} testid={`pt-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.support_types.includes(t)} onClick={() => set("support_types", toggle(form.support_types, t))}>{t}</Chip>
            ))}
          </div>
          {form.support_types.includes("Narrative Therapy") && (
            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-4">What type of session?</p>
              <div className="flex flex-wrap gap-3">
                {SESSION_TYPES.map((t) => (
                  <Chip key={t} testid={`pt-session-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.session_type === t} onClick={() => set("session_type", form.session_type === t ? "" : t)}>{t}</Chip>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">How would you like to connect?</h3>
          <div className="flex flex-wrap gap-3 mb-8">
            {CONNECT.map((t) => (
              <Chip key={t} testid={`pt-connect-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.connect === t} onClick={() => set("connect", form.connect === t ? "" : t)}>{t}</Chip>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Preferred days" testid="pt-days-input" optional value={form.preferred_days} onChange={setEv("preferred_days")} placeholder="e.g. weekdays, Saturdays" />
            <Field label="Preferred times" testid="pt-times-input" optional value={form.preferred_times} onChange={setEv("preferred_times")} placeholder="e.g. after school, mornings — or first available" />
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">What would you like support with?</h3>
          <div className="flex flex-wrap gap-3 mb-8">
            {AREAS.map((t) => (
              <Chip key={t} testid={`pt-area-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.support_areas.includes(t)} onClick={() => set("support_areas", toggle(form.support_areas, t))}>{t}</Chip>
            ))}
          </div>
          <TextArea label="Is there anything you'd like us to know?" testid="pt-anything-textarea" optional value={form.anything} onChange={setEv("anything")} />
          <p className="mt-3 text-sm text-earth/70">You don't need to tell us your whole story here. Share only what you're comfortable sharing.</p>
        </div>
      )}

      {step === 4 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">Who would you feel comfortable working alongside?</h3>
          <div className="flex flex-wrap gap-3 mb-8">
            {PRAC_CHOICE.map((t) => (
              <Chip key={t} testid={`pt-choice-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.practitioner_choice === t} onClick={() => set("practitioner_choice", form.practitioner_choice === t ? "" : t)}>{t}</Chip>
            ))}
          </div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-4">Optional preferences</p>
          <div className="flex flex-wrap gap-3">
            {PRAC_PREFS.map((t) => (
              <Chip key={t} testid={`pt-pref-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.practitioner_preferences.includes(t)} onClick={() => set("practitioner_preferences", toggle(form.practitioner_preferences, t))}>{t}</Chip>
            ))}
          </div>
          <p className="mt-5 text-sm text-earth/70">We'll do our best to match your preferences with practitioner availability.</p>
        </div>
      )}

      {step === 5 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">Your contact details</h3>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Name" testid="pt-name-input" value={form.name} onChange={setEv("name")} required />
            <Field label="Preferred name" testid="pt-preferred-name-input" optional value={form.preferred_name} onChange={setEv("preferred_name")} />
            <Field label="Pronouns" testid="pt-pronouns-input" optional value={form.pronouns} onChange={setEv("pronouns")} />
            <Field label="Age / age range" testid="pt-age-input" optional value={form.age_range} onChange={setEv("age_range")} />
            <Field label="Email" testid="pt-email-input" type="email" value={form.email} onChange={setEv("email")} required />
            <Field label="Phone" testid="pt-phone-input" optional value={form.phone} onChange={setEv("phone")} />
            <Field label="Preferred contact method" testid="pt-contact-method-input" optional value={form.preferred_contact} onChange={setEv("preferred_contact")} />
          </div>
          <label className="mt-8 flex items-center gap-3 cursor-pointer" data-testid="pt-involves-child-wrap">
            <input data-testid="pt-involves-child-checkbox" type="checkbox" checked={form.involves_child} onChange={setEv("involves_child")} className="h-5 w-5 accent-[#C05746]" />
            <span className="text-sm font-semibold text-earth">This support involves a child or young person</span>
          </label>
          {form.involves_child && (
            <div className="mt-6 grid sm:grid-cols-2 gap-6 rounded-3xl bg-cream border border-ochre/30 p-6">
              <Field label="Child's name" testid="pt-child-name-input" optional value={form.child_name} onChange={setEv("child_name")} />
              <Field label="Child's age" testid="pt-child-age-input" optional value={form.child_age} onChange={setEv("child_age")} />
              <Field label="Parent/carer name" testid="pt-parent-name-input" optional value={form.parent_carer_name} onChange={setEv("parent_carer_name")} />
              <Field label="Relationship to child" testid="pt-relationship-input" optional value={form.relationship_to_child} onChange={setEv("relationship_to_child")} />
            </div>
          )}
          <p className="mt-5 text-sm text-earth/70">Please don't include detailed trauma histories here — there's space for that in the relationship, not the form.</p>
        </div>
      )}

      {step === 6 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">Almost done</h3>
          <label className="flex items-start gap-4 rounded-3xl bg-cream border border-ochre/35 px-6 py-5 cursor-pointer" data-testid="pt-confirm-wrap">
            <input data-testid="pt-confirm-checkbox" type="checkbox" checked={form.confirm} onChange={setEv("confirm")} className="mt-1 h-5 w-5 accent-[#C05746]" />
            <span className="text-sm leading-relaxed text-earth">
              I understand that this is a request for support and my appointment is not confirmed until Ngawilyi
              Narrative contacts me.
            </span>
          </label>
          <PolicyLinks />
          <CrisisNote />
        </div>
      )}

      <StepNav onBack={step > 1 ? () => setStep(step - 1) : null} onNext={next} nextLabel={step === 6 ? "Request My Session" : "Continue"} busy={busy} isLast={step === 6} />
    </div>
  );
};

const PrivateSupports = () => {
  const [preset, setPreset] = useState(null);
  const [showSessions, setShowSessions] = useState(false);
  const [showTopics, setShowTopics] = useState(false);

  const go = (types) => {
    setPreset(types);
    document.getElementById("request-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
  <div data-testid="private-supports-page">
    <PageHero
      kicker="Private Supports"
      title={"Private Narrative Therapy & Support.\nNo service referral needed."}
      lede="Sometimes you don't need another system or service involved. You might simply need a safe space, the right person and a good yarn. Ngawilyi provides private support for individuals, children and young people, couples, parents/carers and families — because people are not problems to be fixed."
    >
      <div className="mt-10 flex flex-wrap gap-4">
        <a data-testid="private-hero-request-btn" href="#request-form" className="rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">
          Request a Private Session
        </a>
        <a data-testid="private-hero-meet-btn" href="#practitioners" className="rounded-full border-2 border-charcoal px-8 py-[14px] text-sm font-bold uppercase tracking-widest text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-cream">
          Meet Our Narrative Therapists
        </a>
      </div>
    </PageHero>

    {/* THREE SERVICE AREAS */}
    <section className="px-6 py-14 sm:py-20 bg-cream">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-12">
          <Kicker>Our private supports</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">Three ways in. <span className="italic text-terracotta">One relationship.</span></h2>
        </Reveal>
        <div className="space-y-8">
          <Reveal>
            <div className="rounded-[40px] bg-terracotta text-cream p-10 sm:p-14 grain relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-cream/10 organic-blob blur-2xl" />
              <div className="relative">
                <BookOpen className="h-8 w-8 text-cream/80" strokeWidth={1.5} />
                <h3 className="mt-5 font-display font-light text-4xl">Narrative Therapy</h3>
                <p className="mt-2 font-display italic text-2xl text-cream/85">Your life is bigger than the problem.</p>
                <p className="mt-5 text-cream/85 leading-relaxed max-w-3xl">
                  Narrative Therapy creates space to explore the stories people hold about themselves, their
                  relationships, experiences, identity and hopes for the future. Narrative Therapists work
                  alongside people — never positioning themselves as the expert on somebody else's life.
                </p>
                <button
                  type="button"
                  data-testid="session-topics-toggle"
                  onClick={() => setShowTopics((s) => !s)}
                  className="mt-6 text-xs uppercase tracking-[0.25em] font-bold text-cream/80 hover:text-cream transition-colors"
                >
                  Narrative conversations might explore {showTopics ? "— hide" : "— view topics"}
                </button>
                {showTopics && (
                  <div className="mt-4 flex flex-wrap gap-2.5" data-testid="session-topics-list">
                    {["Identity and belonging", "Confidence and self-worth", "Relationships", "Family experiences", "Stress and overwhelm", "Grief and loss", "Life transitions", "Experiences of trauma", "Culture and connection", "Parenting and caring", "Children's emotional wellbeing", "Education and school experiences", "Experiences with systems and services", "Strengths, skills and knowledge", "Preferred ways of being", "Hopes for the future"].map((t) => (
                      <span key={t} className="rounded-full bg-cream/12 border border-cream/25 px-4 py-2 text-[13px] font-semibold text-cream/90">{t}</span>
                    ))}
                  </div>
                )}
                <button type="button" data-testid="request-narrative-therapy-btn" onClick={() => go(["Narrative Therapy"])} className="mt-8 inline-flex rounded-full bg-cream px-8 py-4 text-sm font-bold uppercase tracking-widest text-terracotta transition-transform duration-300 hover:scale-105">Request Narrative Therapy</button>
              </div>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-8">
            <Reveal delay={0.1}>
              <div className="rounded-[40px] bg-creamdark border border-ochre/25 p-10 sm:p-12 h-full">
                <Compass className="h-8 w-8 text-terracotta" strokeWidth={1.5} />
                <h3 className="mt-5 font-display font-light text-3xl text-charcoal">Mentoring</h3>
                <p className="mt-2 font-display italic text-xl text-terracotta">Sometimes support looks like having the right person beside you.</p>
                <p className="mt-4 text-earth/85 leading-relaxed">
                  Ngawilyi mentoring takes relational and narrative ideas beyond the traditional therapy room —
                  building confidence, practising skills, strengthening relationships and working towards personal
                  goals through everyday experiences.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {["Confidence", "Identity", "Communication", "Social connection", "Life skills", "Building routines", "Education and transitions", "Community connection", "Independence", "Exploring interests", "Goal setting", "A trusted person to yarn with"].map((t) => (
                    <span key={t} className="rounded-full bg-cream border border-ochre/40 px-3.5 py-1.5 text-xs font-semibold text-earth">{t}</span>
                  ))}
                </div>
                <button type="button" data-testid="request-mentoring-btn" onClick={() => go(["Mentoring"])} className="mt-7 inline-flex rounded-full bg-terracotta px-7 py-3.5 text-xs font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">Request Mentoring</button>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="rounded-[40px] bg-charcoal text-cream p-10 sm:p-12 h-full grain relative overflow-hidden">
                <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-ochre/20 organic-blob blur-2xl" />
                <div className="relative">
                  <Footprints className="h-8 w-8 text-ochre" strokeWidth={1.5} />
                  <h3 className="mt-5 font-display font-light text-3xl">Fitness & Movement</h3>
                  <p className="mt-2 font-display italic text-xl text-ochre">Strength through movement. Connection through doing.</p>
                  <p className="mt-4 text-cream/80 leading-relaxed">
                    Not everybody connects best sitting across from someone in a room. Movement can create another
                    space for connection, confidence, routine and conversation — incorporated into private
                    mentoring and support.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {["Walking while yarning", "General fitness and movement", "Outdoor activities", "Movement-based mentoring", "Confidence around physical activity", "Healthy routines", "Goal setting and motivation", "Trying new activities", "Community recreation"].map((t) => (
                      <span key={t} className="rounded-full bg-cream/10 border border-cream/20 px-3.5 py-1.5 text-xs font-semibold text-cream/85">{t}</span>
                    ))}
                  </div>
                  <button type="button" data-testid="request-movement-btn" onClick={() => go(["Fitness & Movement"])} className="mt-7 inline-flex rounded-full bg-terracotta px-7 py-3.5 text-xs font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">Request Fitness & Movement Support</button>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    {/* SESSION TYPES */}
    <section className="px-6 py-12 bg-cream">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <Kicker>Narrative Therapy session types</Kicker>
          <button
            type="button"
            data-testid="session-types-toggle"
            onClick={() => setShowSessions((s) => !s)}
            className="text-xs font-bold uppercase tracking-widest text-terracotta underline decoration-ochre decoration-2 underline-offset-8 hover:text-earth transition-colors"
          >
            {showSessions ? "Hide session types" : "View the five session types"}
          </button>
        </Reveal>
        {showSessions && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5" data-testid="session-types-grid">
            {[
              ["One-on-One — Adults", "Space to unpack experiences, relationships, identity, change, challenges and preferred directions for life."],
              ["Children & Young People", "Age-appropriate conversations, storytelling, creativity and activities. Young people are knowledge holders in their own lives — never defined by behaviours, diagnoses or problems."],
              ["Couples", "Exploring communication, connection, conflict and change — and what has been getting between the couple and the relationship they would prefer."],
              ["Family", "Space for different perspectives to be heard while strengthening relationships and the knowledge already within the family."],
              ["Parent/Carer & Child", "Relational sessions exploring communication, big feelings, difficult experiences and the strengths already present in the relationship."],
            ].map(([t, d], i) => (
              <div key={t} data-testid={`session-type-${i}`} className="rounded-[28px] bg-creamdark border border-ochre/25 p-7 h-full transition-transform duration-300 hover:-translate-y-1">
                <span className="font-display italic text-terracotta/60 text-lg">{String(i + 1).padStart(2, "0")}</span>
                <h4 className="mt-2 font-semibold text-charcoal leading-snug">{t}</h4>
                <p className="mt-2 text-[13px] leading-relaxed text-earth/80">{d}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>

    {/* MIX YOUR SUPPORT */}
    <section className="px-6 py-14 sm:py-20 bg-creamdark grain relative overflow-hidden">
      <div className="absolute -top-20 -left-20 w-80 h-80 bg-terracotta/10 organic-blob blur-3xl" />
      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <Kicker>Support doesn't have to fit one box</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Mix support in a way that <span className="italic text-terracotta">works for you.</span>
          </h2>
          <div className="mt-8 flex flex-wrap gap-3">
            {["Narrative Therapy + Mentoring", "Narrative Therapy + Movement", "Mentoring + Movement", "A combination of all three"].map((t) => (
              <button key={t} type="button" data-testid={`mix-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => go(["Combination"])} className="rounded-full bg-cream border border-ochre/40 px-5 py-2.5 text-sm font-semibold text-earth transition-all duration-300 hover:border-terracotta hover:text-terracotta">
                {t}
              </button>
            ))}
          </div>
          <p className="mt-8 text-base leading-relaxed text-earth">
            Ngawilyi adapts the way support happens around the person — rather than expecting the person to adapt
            to the service.
          </p>
        </Reveal>
        <div className="space-y-4">
          {[
            "A young person might prefer having a yarn while walking.",
            "Someone might work on confidence through mentoring, then explore the stories influencing that confidence through Narrative Therapy.",
            "Another person might connect better while participating in something they enjoy.",
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="rounded-3xl bg-cream border border-ochre/30 px-7 py-6 font-display text-xl sm:text-2xl font-light text-earth">{s}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* WHAT CAN WE YARN ABOUT */}
    <section className="px-6 py-14 sm:py-20 bg-cream">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-8">
          <Kicker>What can we yarn about?</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-3xl">
            You don't need a diagnosis or a perfectly defined "problem".
          </h2>
          <p className="mt-6 font-display font-light text-2xl sm:text-3xl text-charcoal max-w-3xl">
            Not sure what to call what's happening?{" "}
            <span className="italic text-terracotta">That's okay. Tell us in your own words.</span>
          </p>
          <button
            type="button"
            data-testid="yarn-topics-toggle"
            onClick={() => setShowTopics((s) => !s)}
            className="mt-6 text-xs font-bold uppercase tracking-widest text-terracotta underline decoration-ochre decoration-2 underline-offset-8 hover:text-earth transition-colors"
          >
            {showTopics ? "Hide topics" : "Browse things people yarn about"}
          </button>
        </Reveal>
        {showTopics && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="yarn-topics-grid">
            {["Relationships", "Family", "Parenting/caring", "Identity and belonging", "Culture and connection", "Grief and loss", "Stress and overwhelm", "Confidence", "Children's wellbeing", "Behaviour affecting relationships", "Education/school", "Communication", "Trauma and difficult experiences", "Life changes", "Systems/service experiences", "Social connection", "Building routines", "Something else"].map((t, i) => (
              <div key={t} data-testid={`yarn-topic-${i}`} className="rounded-3xl bg-creamdark border border-ochre/25 px-5 py-6 h-full flex items-center text-center justify-center transition-all duration-300 hover:border-terracotta hover:-translate-y-1">
                <p className="text-sm font-semibold text-earth">{t}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>

    {/* REQUEST FORM */}
    <section id="request-form" className="px-6 py-14 sm:py-20 bg-cream scroll-mt-24">
      <div className="max-w-4xl mx-auto">
        <Reveal className="mb-10">
          <Kicker>Request a Session</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Tell us what you're looking for.
          </h2>
          <p className="mt-5 text-earth/85 leading-relaxed max-w-2xl">
            We'll help match you with an appropriate Narrative Therapist or practitioner. This is a request, not
            an instant booking — a real person will yarn with you before anything is confirmed.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <PrivateRequestForm preset={preset} />
        </Reveal>
      </div>
    </section>

    {/* FEES */}
    <section id="fees" className="px-6 py-14 sm:py-20 bg-creamdark grain relative overflow-hidden scroll-mt-24">
      <div className="relative max-w-5xl mx-auto">
        <Reveal className="mb-10">
          <Kicker>Private Support Fees</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Private Narrative Therapy <span className="italic text-terracotta">– 1 Hour Sessions.</span>
          </h2>
          <p className="mt-5 text-base lg:text-lg leading-relaxed text-earth max-w-2xl">
            Our private fees are intentionally set to be accessible to community. We believe cost shouldn't
            unnecessarily create another barrier to accessing therapeutic and relational support.
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            ["Initial yarn / connection call", "Free", "15 mins"],
            ["1:1 Narrative Support", "$110", "60 mins"],
            ["Child / Young Person Narrative Therapy", "$110", "60 mins"],
            ["Family Narrative Session", "$130", "75–90 mins"],
          ].map(([t, p, per]) => (
            <div key={t} data-testid={`fee-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-3xl bg-cream border border-ochre/30 px-7 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="font-semibold text-charcoal">{t}</p>
              <p className="sm:text-right">
                <span className="font-display italic text-2xl text-terracotta">{p}</span>
                <span className="block text-[11px] uppercase tracking-[0.15em] font-bold text-earth/60">{per}</span>
              </p>
            </div>
          ))}
        </div>

        <Reveal className="mt-14 mb-8">
          <h3 className="font-display font-light text-3xl sm:text-4xl text-charcoal">Other Private Supports</h3>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            ["Child & Young Person Mentoring", "$100", "60 mins"],
            ["Mentoring + Narrative Practice", "$110", "60 mins"],
            ["Fitness + Mentoring", "$100", "60 mins"],
            ["Foster/Kinship Carer Support", "$110", "60 mins"],
            ["Substance Use / Turning Story support", "$110", "60 mins"],
            ["Family Restoration Support", "$120", "per hour"],
            ["Professional/Case Consultation", "$130", "per hour"],
            ["Community consultation", "From $120", "per hour or quoted package"],
            ["Training & organisational work", "Quoted", "separately"],
          ].map(([t, p, per]) => (
            <div key={t} data-testid={`fee-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-3xl bg-cream border border-ochre/30 px-7 py-6">
              <p className="font-semibold text-charcoal">{t}</p>
              <p className="mt-2 font-display italic text-3xl text-terracotta">{p}</p>
              <p className="text-xs uppercase tracking-[0.18em] font-bold text-earth/60 mt-1">{per}</p>
            </div>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div data-testid="arabana-discount" className="mt-10 rounded-[36px] bg-terracotta text-cream p-9 sm:p-12 grain relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-cream/10 organic-blob blur-2xl" />
            <div className="relative">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-cream/70 mb-4">For Arabana mob</p>
              <p className="font-display font-light text-2xl sm:text-3xl leading-snug">
                If you identify as Arabana, an automatic community discount applies to Ngawilyi private support fees.
              </p>
              <p className="mt-5 text-cream/85 leading-relaxed max-w-2xl">
                You don't need to explain or justify why you need the reduced rate — just let us know you're
                Arabana when getting in touch. The discount applies to privately paid services, not externally
                funded services such as NDIS, government, school or organisational funding.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          {[
            ["Session duration", "Narrative therapy sessions are 1 hour (family sessions 75–90 mins). Mentoring and movement supports are charged per hour."],
            ["Cancellation policy", "Shared in plain English before support begins — life happens, we get it."],
            ["Payment information", "Payment options and invoicing confirmed when your session is booked."],
          ].map(([t, d]) => (
            <div key={t} className="rounded-3xl bg-cream border border-ochre/30 px-7 py-6">
              <p className="font-semibold text-charcoal">{t}</p>
              <p className="mt-2 text-sm text-earth/75 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-earth/75">
          See the full list on our{" "}
          <Link to="/pricing" data-testid="fees-full-pricing-link" className="font-bold text-terracotta underline decoration-ochre decoration-2 underline-offset-4 hover:text-earth transition-colors">
            Pricing & Fees page
          </Link>.
        </p>
      </div>
    </section>

    <div id="practitioners" className="scroll-mt-24">
      <Practitioners kicker="Meet Our Narrative Therapists" title="Warm, qualified, beside you." requestLink="#request-form" />
    </div>

    <ClosingMessage />
  </div>
  );
};

export default PrivateSupports;
